import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import type { Tone } from '@/components/ui/Tone'

// Категории сопровождения франчайзи (решение владельца 09.10.2026). Что положено категории,
// решает сервер (partner_service_category_provides); здесь только подписи для экрана.
export type PartnerServiceCategory = 1 | 2 | 3 | 4

export const PARTNER_SERVICE_CATEGORIES: ReadonlyArray<{
  value: PartnerServiceCategory
  label: string
  services: string
  tone: Tone
}> = [
  { value: 1, label: 'Категория 1', services: 'чаты, специалисты, ТУ, ЛМ — платят и лояльны', tone: 'success' },
  { value: 2, label: 'Категория 2', services: 'специалисты, ТУ, ЛМ — платят, но не совсем лояльны', tone: 'info' },
  { value: 3, label: 'Категория 3', services: 'специалисты, ТУ, ЛМ — не платят, но лояльны', tone: 'warn' },
  { value: 4, label: 'Категория 4', services: 'только ЛМ — красная зона: отсмотры не выдаются', tone: 'danger' },
]

export function partnerServiceCategoryMeta(category: number | null | undefined) {
  return PARTNER_SERVICE_CATEGORIES.find(c => c.value === category) ?? null
}

export interface PartnerServiceCategoryRow {
  partner_number: number
  change_id: number | null
  category: PartnerServiceCategory | null
  valid_from: string | null
  valid_to: string | null
  reason: string | null
  review_on: string | null
  set_by_name: string | null
  updated_at: string | null
  next_category: PartnerServiceCategory | null
  next_from: string | null
}

export interface PartnerServiceCategoryHistoryRow {
  change_id: number
  category: PartnerServiceCategory
  valid_from: string
  valid_to: string | null
  reason: string
  review_on: string | null
  set_by_name: string | null
  closed_by_name: string | null
  created_at: string
  updated_at: string
}

export type PartnerServiceCategoryAction = 'created' | 'changed' | 'replaced' | 'unchanged'

export interface PartnerServiceCategorySetResult {
  partner_number: number
  action: PartnerServiceCategoryAction
  previous_category: PartnerServiceCategory | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; code?: string }
    if (e.code === '23P01') {
      return 'Интервалы категорий франчайзи пересекаются. Обновите страницу и проверьте историю.'
    }
    if (e.message) return e.message
  }
  return String(error)
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  // RPC нет в сгенерированных типах Supabase — вызываем по имени.
  const call = getSupabase().rpc.bind(getSupabase()) as unknown as (
    name: string,
    params?: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: unknown }>
  const { data, error } = await withAuthRecovery(() => call(fn, args))
  if (error) throw new Error(formatError(error))
  return data as T
}

export async function listPartnerServiceCategories(): Promise<PartnerServiceCategoryRow[]> {
  return (await rpc<PartnerServiceCategoryRow[]>('list_partner_service_categories')) ?? []
}

export async function getPartnerServiceCategoryHistory(
  partnerNumber: number,
): Promise<PartnerServiceCategoryHistoryRow[]> {
  return (await rpc<PartnerServiceCategoryHistoryRow[]>('get_partner_service_category_history', {
    p_partner_number: partnerNumber,
  })) ?? []
}

export async function setPartnerServiceCategory(params: {
  partnerNumbers: number[]
  category: PartnerServiceCategory
  effectiveFrom: string
  reason: string
  reviewOn?: string | null
}): Promise<PartnerServiceCategorySetResult[]> {
  return (await rpc<PartnerServiceCategorySetResult[]>('set_partner_service_category', {
    p_partner_numbers: params.partnerNumbers,
    p_category: params.category,
    p_effective_from: params.effectiveFrom,
    p_reason: params.reason,
    p_review_on: params.reviewOn || null,
  })) ?? []
}

export async function cancelPartnerServiceCategoryChange(changeId: number): Promise<void> {
  await rpc('cancel_partner_service_category_change', { p_change_id: changeId })
}
